import { memo } from 'react';
import ButtonCounter from '../ButtonCounter';
import {
  AddToShoppingCartContext,
  AddToShoppingCartSource,
  ShoppingCartProductType,
} from '@monofront/types';
import AddCart from '@monofront/design-system/visuals/Icons/Old/AddCart';
import useProductToShoppingCart from '@hooks/cart/useProductToShoppingCart';
import { useEligibility } from '@monofront/business/contexts';
import {
  getIsFresh,
  getIsUltraFresh,
  getMinimumProductQuantity,
} from '@monofront/business/utils';

interface IButtonAddToCartProps {
  shoppingCartProduct: ShoppingCartProductType;
  max: number;
  size?: 'medium' | 'large';
  containerContext?: AddToShoppingCartContext;
  addToShoppingCartInsight?: () => void;
  hasEditViewOnly?: boolean;
  isDisabled?: boolean;
  editCountCallback?: (newCount: number) => Promise<void>;
  isReplacement?: boolean;
  UNSAFE_className?: string;
  children?: React.ReactNode;
}

const ButtonAddToCart: React.FC<IButtonAddToCartProps> = ({
  shoppingCartProduct,
  max,
  size,
  containerContext = { source: AddToShoppingCartSource.DEFAULT },
  addToShoppingCartInsight,
  hasEditViewOnly = false,
  isDisabled = false,
  editCountCallback,
  isReplacement = false,
  UNSAFE_className = '',
  children,
}) => {
  const { eligibility } = useEligibility();

  const { currentProductQuantity, handleEditCount, isLoading } =
    useProductToShoppingCart(
      shoppingCartProduct,
      containerContext,
      addToShoppingCartInsight,
      isReplacement,
    );

  const needsFreshVerification =
    (getIsFresh(shoppingCartProduct.tags) && !eligibility.fresh) ||
    (getIsUltraFresh(shoppingCartProduct.tags) && !eligibility.ultraFresh);
  const needsEligibilityVerification = needsFreshVerification;
  const minimumProductQuantity = getMinimumProductQuantity(
    shoppingCartProduct.tags,
  );

  return (
    <ButtonCounter
      currentQuantity={currentProductQuantity}
      max={max}
      editCount={async (oldCount, newCount) => {
        const canIncrement = await handleEditCount(oldCount, newCount);

        if (!canIncrement) {
          return;
        }

        await editCountCallback?.(newCount);
      }}
      size={size}
      isLoading={isLoading}
      hasEditViewOnly={hasEditViewOnly}
      isDisabled={isDisabled}
      eligibilityShouldBeVerified={needsEligibilityVerification}
      UNSAFE_className={UNSAFE_className}
      minimumProductQuantity={minimumProductQuantity}
    >
      {children ? (
        children
      ) : (
        <AddCart
          width={size === 'large' ? 40 : 32}
          height={size === 'large' ? 40 : 32}
        />
      )}
    </ButtonCounter>
  );
};

export default memo(ButtonAddToCart);
